"use client";

import { useRef } from "react";
import type { ChatShareProvider } from "@/src/lib/chat-share/types";
import type { PdfFontKey } from "@/src/lib/pdf/fonts";
import type { PdfOptions } from "@/src/lib/pdf/print-document";
import type { EditorPreferencesSource } from "@/src/lib/storage/user-preferences";
import { ChatShareImportToolbar } from "./chat-share/chat-share-import-toolbar";
import { embeddedConverterShellClassName } from "./home/embedded-converter-shell";
import { SiteHeader } from "./home/site-header";
import { EditorWorkspace, type EditorWorkspaceHandle } from "./editor-workspace";
import { RepoMarkdownImportToolbar } from "./repo-markdown/repo-markdown-import-toolbar";
import { classNames } from "./ui/class-names";

export type EditorShellMode = "embedded" | "fullscreen";

export function EditorShell({
  mode,
  title,
  initialMarkdown,
  initialFilename,
  editorSource = "editor",
  initialPdfOptions,
  initialEditorFontFamily,
  initialEditorFontSize,
  chatShareProvider,
  repoMarkdownImport = false,
}: Readonly<{
  mode: EditorShellMode;
  title?: string;
  initialMarkdown?: string;
  initialFilename?: string;
  editorSource?: EditorPreferencesSource;
  initialPdfOptions?: PdfOptions;
  initialEditorFontFamily?: PdfFontKey;
  initialEditorFontSize?: number;
  /** Show ChatGPT/Gemini share import above the fullscreen workspace. */
  chatShareProvider?: ChatShareProvider;
  /** Show repository Markdown import above the fullscreen workspace. */
  repoMarkdownImport?: boolean;
}>) {
  const workspaceRef = useRef<EditorWorkspaceHandle | null>(null);
  const isEmbedded = mode === "embedded";
  const shellClassName = isEmbedded
    ? embeddedConverterShellClassName
    : classNames(
        "flex min-h-0 flex-col overflow-hidden bg-zinc-100 text-zinc-950",
        "h-dvh md:min-h-[40rem]",
      );

  const workspace = (
    <EditorWorkspace
      ref={workspaceRef}
      variant={mode}
      initialMarkdown={initialMarkdown}
      initialFilename={initialFilename}
      editorSource={editorSource}
      initialPdfOptions={initialPdfOptions}
      initialEditorFontFamily={initialEditorFontFamily}
      initialEditorFontSize={initialEditorFontSize}
    />
  );

  if (isEmbedded) {
    return (
      <div data-app-shell-mode="embedded" className={shellClassName}>
        {workspace}
      </div>
    );
  }

  return (
    <main data-app-shell-mode="fullscreen" className={shellClassName}>
      <SiteHeader />
      {title ? (
        <h1 className="min-w-0 shrink-0 truncate border-b border-zinc-200 bg-white px-3 py-2 text-center text-sm font-semibold text-zinc-600 sm:px-4">
          {title}
        </h1>
      ) : (
        <h1 className="sr-only">Fullscreen Markdown to PDF Editor</h1>
      )}
      {repoMarkdownImport ? (
        <RepoMarkdownImportToolbar
          workspaceRef={workspaceRef}
          workspaceReady
          consumeDraft
          inputId="readme-fullscreen-repo-url"
        />
      ) : chatShareProvider ? (
        <ChatShareImportToolbar
          provider={chatShareProvider}
          workspaceRef={workspaceRef}
          workspaceReady
          consumeDraft
          inputId={`${chatShareProvider}-fullscreen-share-url`}
        />
      ) : null}
      {workspace}
    </main>
  );
}
